import { useState, useEffect, useCallback, useRef } from 'react';

import Provision from '../api/provision';

import useApiQuery from './useApiQuery';

import type { VirtualCloud } from '@/types/cloudTypes';

const useCloudQuota = (juiceGroup: string) => {
  const [availableVirtualClouds, setAvailableVirtualClouds] = useState<VirtualCloud[]>([]);
  const { makeApiCall: getVirtualCloudsCall } = useApiQuery(Provision.fetchVirtualClouds);
  const { makeApiCall: fetchNetworkPoliciesCall } = useApiQuery(
    (juiceGroup: unknown, cloudId: unknown) => Provision.fetchNetworkPolicies(juiceGroup, cloudId)
  );

  const getAvailableClouds = useCallback(async (): Promise<void> => {
    if (!juiceGroup) return;

    try {
      const data = (await getVirtualCloudsCall({ juiceGroups: [juiceGroup] })) as VirtualCloud[];
      if (Array.isArray(data)) {
        setAvailableVirtualClouds(
          data.map((virtualCloud: VirtualCloud) => ({
            ...virtualCloud,
            unusedQuota: {
              compute: {
                cores:
                  virtualCloud.allowed_quota.compute.cores - virtualCloud.used_quota.compute.cores,
                ram:
                  (virtualCloud.allowed_quota.compute.ram - virtualCloud.used_quota.compute.ram) /
                  1024,
              },
              volume: {
                gigabytes:
                  virtualCloud.allowed_quota.volume.gigabytes -
                  virtualCloud.used_quota.volume.gigabytes,
              },
              network: {
                floatingIps:
                  virtualCloud.allowed_quota.network.floating_ips -
                  virtualCloud.used_quota.network.floating_ips,
              },
            },
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching virtual clouds:', error);
    }
  }, [juiceGroup, getVirtualCloudsCall]);

  const getExternalNetworks = useCallback(
    async (cloudName: string): Promise<string[]> => {
      if (!juiceGroup || !cloudName) return [];

      try {
        const data = (await fetchNetworkPoliciesCall(juiceGroup, cloudName)) as
          { access_as_external: { network_name: string }[] }[] | undefined;
        return data?.[0]?.access_as_external.map(external => external.network_name) ?? [];
      } catch (error) {
        console.error('Error fetching network policies:', error);
        return [];
      }
    },
    [juiceGroup, fetchNetworkPoliciesCall]
  );

  const getAvailableCloudsRef = useRef(getAvailableClouds);
  getAvailableCloudsRef.current = getAvailableClouds;

  useEffect(() => {
    getAvailableCloudsRef.current();
  }, [juiceGroup]);

  return {
    availableVirtualClouds,
    getExternalNetworks,
  };
};

export default useCloudQuota;
